import type {
  WebAnalyticsPolicySource,
  WebAnalyticsRegionPolicy,
  WebAnalyticsRegionResult,
} from './web-analytics-consent.models';

const CONSENT_REQUIRED_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HU', 'IE', 'IS', 'IT',
  'LI', 'LT', 'LU', 'LV', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'GB', 'CH',
]);

type RuntimeConfigWindow = typeof globalThis & {
  __INVEMORY_RUNTIME_CONFIG__?: {
    analyticsCountryCode?: unknown;
    analyticsPolicy?: unknown;
  };
};

function normalizePolicy(value: unknown): WebAnalyticsRegionPolicy | null {
  return value === 'required' || value === 'defaultOn' || value === 'unknown' ? value : null;
}

export function resolveWebAnalyticsRegionPolicy(
  globalScope: typeof globalThis = globalThis,
  documentScope: Document | null = typeof document === 'undefined' ? null : document,
): WebAnalyticsRegionResult {
  if (!documentScope) {
    return {countryCode: null, policy: 'unknown', source: 'unavailable'};
  }

  const runtimeConfig = (globalScope as RuntimeConfigWindow).__INVEMORY_RUNTIME_CONFIG__;
  const runtimePolicy = normalizePolicy(runtimeConfig?.analyticsPolicy);
  if (runtimePolicy) {
    return {countryCode: null, policy: runtimePolicy, source: 'runtimePolicy'};
  }

  const runtimeCountry = typeof runtimeConfig?.analyticsCountryCode === 'string'
    ? runtimeConfig.analyticsCountryCode
    : null;
  const metaCountry = documentScope
    .querySelector<HTMLMetaElement>('meta[name="invemory-country-code"]')
    ?.content ?? null;
  const rawCountry = runtimeCountry ?? metaCountry;
  const countryCode = rawCountry?.trim().toUpperCase() ?? '';
  const source: WebAnalyticsPolicySource = runtimeCountry ? 'runtimeCountry' : metaCountry ? 'metaCountry' : 'unavailable';

  if (!/^[A-Z]{2}$/.test(countryCode)) {
    return {countryCode: null, policy: 'unknown', source};
  }

  return {
    countryCode,
    policy: CONSENT_REQUIRED_COUNTRIES.has(countryCode) ? 'required' : 'defaultOn',
    source,
  };
}
